<template>
  <FormSubmitPopup
    :title="$t('dayEndClosing')"
    v-model="value"
    @popupClose="$emit('input', false)"
    hideRgtBtn
    large>
    <template v-slot:input>
      <v-container py-0 fluid>
        <v-layout align-baseline row>
          <!-- control -->
          <v-flex xs8>
            <v-layout  align-end>
              <v-flex mr-2 xs3>
                <v-menu
                  ref="datePicker"
                  v-model="datePicker"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="date"
                      :label="$t('dayEndClosing')"
                      prepend-icon="event"
                      v-on="on"
                      readonly
                    >
                    </v-text-field>
                  </template>
                  <v-date-picker v-model="date"  no-title scrollable>
                  </v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex mx-2 xs4>
                <v-combobox
                  :label="$t('organizations')"
                  :items="clients"
                  v-model="selectedClients"
                  chipText="clientName"
                  itemText="clientName"
                  chips
                  multiple
                  clearable 
                  return-object/>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex text-xs-right>
            <v-btn color="primary" :loading="isLoading" @click.stop="getDayEndClosings()">{{ $t('search') }}</v-btn>
            <v-btn color="primary" :loading="isLoading" :disabled="!items.length" @click.stop="exportDayEndClosings()">{{ $t('export') }}</v-btn>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12>
            <v-data-table
              :headers="headers"
              :items="items"
              class="elevation-1"
              item-key="clientKey"
              :loading="isLoading"
              :pagination.sync="pagination"
            >
              <template v-slot:items="props">
                  <td class="subheading">
                    <div>
                        <v-icon v-if="props.item.type === 'warehouse'" style="font-size:20px">local_shipping</v-icon>
                        <v-icon v-if="props.item.type === 'organization'" style="font-size:20px">business</v-icon>
                        <v-icon v-if="props.item.isBlockCreditCardDeposit" style="font-size:20px">strikethrough_s</v-icon>
                        {{ props.item.clientName }}
                    </div>
                  </td>
                  <td>{{props.item.companyName}}</td>
                  <td>{{props.item.balance}}</td>
                  <td>{{props.item.accountManager}}</td>
                  <td>({{ props.item.email }})</td>
                  <td>{{props.item.dayEndBalance}}</td>
                  <td class="text-xs-center">
                    {{ toDateYYYYMMDD(props.item.lastTransactionTime) }}
                  </td>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
    </template>
  </FormSubmitPopup>
</template>

<script>
import { exportListToXlsx, timeTools } from '@/utils/tools'
import FormSubmitPopup from './FormSubmitPopup.vue'
import SelectWidget from './SelectWidget.vue'

export default {
  name: 'DayEndClosingPopup',
  components: { FormSubmitPopup, SelectWidget },
  mixins: [timeTools],
  data () {
    return {
      dayEndClosings: [],
      selectedClients: [],

      datePicker: false,
      pagination: {
        rowsPerPage: 25
      },
      date: new Date(Date.now() - 86400000).toISOString().slice(0, 10),
      isLoading: false
    }
  },
  computed: {
    clientKeyToClientMap () {
      return Object.fromEntries(this.clients.map(client => [client.clientKey, client]))
    },
    items () {
      return (this.selectedClients && this.selectedClients.length ? 
        this.dayEndClosings.filter(item => this.selectedClients.some(client => client.clientKey === item.clientKey)) : 
        this.dayEndClosings
      )
        .map(dayEndClosing => ({
          ...(this.clientKeyToClientMap[dayEndClosing.clientKey] || {}),
          ...dayEndClosing
        }))
    },
    headers () {
      return [
        { text: this.$t('organization'), value: 'clientName', align: 'left', width: '10%', sortable: false },
        { text: this.$t('companyName'), value: 'companyName', align: 'left', width: '10%', sortable: false },
        { text: this.$t('currentBalance'), value: 'balance', align: 'left', width: '10%', sortable: false },
        { text: this.$t('accountManager'), value: 'accountManager', align: 'left', width: '10%', sortable: false },
        { text: this.$t('email'), value: 'email', align: 'left', width: '10%', sortable: false },
        { text: this.$t('balanceAfterTransaction'), value: 'dayEndBalance', align: 'left', width: '10%', sortable: false },
        { text: this.$t('transactionTime'), value: 'lastTransactionTime', align: 'left', width: '10%', sortable: false }
      ]
    }
  },
  methods: {
    async getDayEndClosings () {
      this.isLoading = true
      this.dayEndClosings = []
      await this.setDayEndClosings(this.selectedClients, 0)
      this.isLoading = false
    },
    async setDayEndClosings () {
      this.dayEndClosings = await this.$store.dispatch('getClientsDayEndingClosings', {
        date: this.date
      })
      if (!this.dayEndClosings.length) {
        alert('No day-end closings data in this date' + (new Date(this.date).getTime() + 86400000 > Date.now() ? '' : ', please contact support'))
      }
    },
    async exportDayEndClosings () {
      this.isLoading = true
      
      exportListToXlsx(
        [
          [
            this.$t('organization'),
            this.$t('companyName'),
            this.$t('email'),
            this.$t('currentBalance'),
            this.$t('accountManager'),
            this.$t('balanceAfterTransaction'),
            this.$t('transactionTime')
          ],
          ...this.items.map(item => [
            item.clientName, 
            item.companyName || '',
            item.email,
            item.balance || 0,
            item.accountManager || '',
            item.dayEndBalance || '',
            item.lastTransactionTime ? this.toDateYYYYMMDDHHmmss(item.lastTransactionTime) : ''
          ])
        ], `${this.date}-Day-end-Closings.csv`
      )
      this.isLoading = false
    }
  },
  props: {
    value: Boolean,
    clients: Array
  }
}
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/DayEndClosingPopup.vue